import { API_PATHS } from '@/constants/api-paths'
import api from '@/lib/api'
import {
  getOrderItemMediaUrlFallback,
  resolveOrderItemMediaUrl,
} from '@/lib/order-item-media'
import type { ApiResponse } from '@/types'

/** Matches backend `UserDocumentStatus` integer values */
export const USER_DOCUMENT_STATUS = {
  APPROVED: 1,
  PENDING: 2,
  REJECTED: 3,
} as const

export type UserDocSectionCatalogEntry = {
  id: number
  document_name: string
  slug?: string | null
}

export type UserDocumentRecord = {
  id: number
  doc_section?: number | null
  document_front?: string | null
  document_back?: string | null
  status?: number | null
  status_label?: string | null
  is_verified?: boolean | null
}

export type UserDocumentSectionRecord = {
  id: number
  document_name: string
  slug?: string | null
  type?: number | null
  type_label?: string | null
  status?: number | null
  org_instance_id?: number | null
  org_instance_external_id?: string | null
  user_group_id?: number | null
  is_system?: boolean
}

type UserDocumentSectionUpsertPayload = {
  documentName: string
  orgInstance: string | number
  slug?: string
  userGroupId?: string | number | null
  type?: number
}

function extractCatalogPayload(payload: unknown): UserDocSectionCatalogEntry[] {
  if (Array.isArray(payload)) {
    return payload as UserDocSectionCatalogEntry[]
  }

  if (!payload || typeof payload !== 'object') {
    return []
  }

  const record = payload as Record<string, unknown>

  if (Array.isArray(record.data)) {
    return record.data as UserDocSectionCatalogEntry[]
  }

  if (Array.isArray(record.results)) {
    return record.results as UserDocSectionCatalogEntry[]
  }

  if (record.data && typeof record.data === 'object') {
    const inner = record.data as Record<string, unknown>
    if (Array.isArray(inner.data)) {
      return inner.data as UserDocSectionCatalogEntry[]
    }
    if (Array.isArray(inner.results)) {
      return inner.results as UserDocSectionCatalogEntry[]
    }
  }

  return []
}

function extractApiData<T>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    return (payload as ApiResponse<T>).data
  }
  return payload as T
}

function extractSectionResults(payload: unknown): UserDocumentSectionRecord[] {
  const data = extractApiData<unknown>(payload)

  if (Array.isArray(data)) {
    return data as UserDocumentSectionRecord[]
  }

  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>
    if (Array.isArray(record.results)) {
      return record.results as UserDocumentSectionRecord[]
    }
    if (record.data && typeof record.data === 'object' && Array.isArray((record.data as Record<string, unknown>).results)) {
      return (record.data as Record<string, unknown>).results as UserDocumentSectionRecord[]
    }
  }

  return []
}

function normalizeUserDocumentMediaUrl(path?: string | null): string | null {
  if (!path?.trim()) {
    return null
  }

  return resolveOrderItemMediaUrl(path)
    ?? getOrderItemMediaUrlFallback(path)
    ?? path.trim()
}

function normalizeUserDocumentRecord(doc: UserDocumentRecord): UserDocumentRecord {
  return {
    ...doc,
    document_front: normalizeUserDocumentMediaUrl(doc.document_front),
    document_back: normalizeUserDocumentMediaUrl(doc.document_back),
  }
}

function unwrapDocumentListPayload(payload: unknown): UserDocumentRecord[] {
  if (Array.isArray(payload)) {
    return payload as UserDocumentRecord[]
  }

  if (!payload || typeof payload !== 'object') {
    return []
  }

  const record = payload as Record<string, unknown>

  if (Array.isArray(record.results)) {
    return record.results as UserDocumentRecord[]
  }

  if (Array.isArray(record.data)) {
    return record.data as UserDocumentRecord[]
  }

  if (record.data && typeof record.data === 'object') {
    const inner = record.data as Record<string, unknown>
    if (Array.isArray(inner.results)) {
      return inner.results as UserDocumentRecord[]
    }
    if (Array.isArray(inner.data)) {
      return inner.data as UserDocumentRecord[]
    }
  }

  return []
}

function extractPagedResults(payload: unknown): UserDocumentRecord[] {
  const candidates = [
    unwrapDocumentListPayload(payload),
  ]

  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    candidates.push(unwrapDocumentListPayload((payload as ApiResponse<unknown>).data))
  }

  for (const candidate of candidates) {
    if (candidate.length > 0) {
      return candidate.map(normalizeUserDocumentRecord)
    }
  }

  return candidates[0]?.map(normalizeUserDocumentRecord) ?? []
}

export const userDocumentsApi = {
  async listCatalog(options?: {
    orgInstance?: string | number | null
    userGroupId?: string | number | null
    userId?: string | number | null
  }): Promise<UserDocSectionCatalogEntry[]> {
    const res = await api.get(API_PATHS.userDocuments.catalogList, {
      params: {
        org_instance: options?.orgInstance || undefined,
        user_group: options?.userGroupId || undefined,
        user: options?.userId || undefined,
      },
    })
    return extractCatalogPayload(res.data)
  },

  async listForUser(userId: string | number): Promise<UserDocumentRecord[]> {
    const res = await api.get(API_PATHS.userDocuments.list, {
      params: { user: userId, page_size: 500 },
    })
    return extractPagedResults(res.data)
  },

  async listSections(options: {
    orgInstance: string | number
    userGroupId?: string | number | null
    search?: string
  }): Promise<UserDocumentSectionRecord[]> {
    const res = await api.get(API_PATHS.userDocuments.sectionList, {
      params: {
        org_instance: options.orgInstance,
        user_group: options.userGroupId || undefined,
        search_value: options.search?.trim() || undefined,
        page_size: 500,
      },
    })
    return extractSectionResults(res.data)
  },

  async createSection(payload: UserDocumentSectionUpsertPayload): Promise<UserDocumentSectionRecord> {
    const res = await api.post(API_PATHS.userDocuments.sectionCreate, {
      document_name: payload.documentName,
      type: payload.type ?? 1,
      org_instance: payload.orgInstance,
      user_group: payload.userGroupId || undefined,
      slug: payload.slug?.trim() || undefined,
      expire_on_date: 'no',
    })
    return extractApiData<UserDocumentSectionRecord>(res.data)
  },

  async updateSection(id: string | number, payload: Partial<UserDocumentSectionUpsertPayload>): Promise<UserDocumentSectionRecord> {
    const res = await api.patch(API_PATHS.userDocuments.sectionUpdate(id), {
      ...(payload.documentName !== undefined ? { document_name: payload.documentName } : {}),
      ...(payload.type !== undefined ? { type: payload.type } : {}),
      ...(payload.slug !== undefined ? { slug: payload.slug?.trim() || '' } : {}),
    })
    return extractApiData<UserDocumentSectionRecord>(res.data)
  },

  async deactivateSection(id: string | number): Promise<void> {
    await api.delete(API_PATHS.userDocuments.sectionRemove(id))
  },

  async create(payload: {
    docSectionId: number
    userId: string | number
    documentFront?: File
    documentBack?: File
  }): Promise<unknown> {
    const form = new FormData()
    form.append('user_doc_section', String(payload.docSectionId))
    form.append('user', String(payload.userId))
    if (payload.documentFront) {
      form.append('document_front', payload.documentFront)
    }
    if (payload.documentBack) {
      form.append('document_back', payload.documentBack)
    }
    const res = await api.post<ApiResponse<unknown>>(API_PATHS.userDocuments.add, form)
    return res.data
  },

  async patch(
    docId: string | number,
    payload: {
      documentFront?: File
      documentBack?: File
      status?: number
    },
  ): Promise<unknown> {
    const hasFiles = Boolean(payload.documentFront ?? payload.documentBack)
    const url = API_PATHS.userDocuments.update(docId)

    if (!hasFiles) {
      const res = await api.patch<ApiResponse<unknown>>(url, {
        ...(payload.status != null ? { status: payload.status } : {}),
      })
      return res.data
    }

    const form = new FormData()
    if (payload.documentFront) {
      form.append('document_front', payload.documentFront)
    }
    if (payload.documentBack) {
      form.append('document_back', payload.documentBack)
    }
    if (payload.status != null) {
      form.append('status', String(payload.status))
    }
    const res = await api.patch<ApiResponse<unknown>>(url, form)
    return res.data
  },

  async removeFront(docId: string | number): Promise<void> {
    await api.delete(API_PATHS.userDocuments.removeFront(docId))
  },

  async removeBack(docId: string | number): Promise<void> {
    await api.delete(API_PATHS.userDocuments.removeBack(docId))
  },

  async approve(docId: string | number): Promise<UserDocumentRecord> {
    const res = await api.patch<ApiResponse<UserDocumentRecord>>(API_PATHS.userDocuments.approve(docId))
    return normalizeUserDocumentRecord(extractApiData<UserDocumentRecord>(res.data))
  },

  async reject(docId: string | number): Promise<UserDocumentRecord> {
    const res = await api.patch<ApiResponse<UserDocumentRecord>>(API_PATHS.userDocuments.reject(docId))
    return normalizeUserDocumentRecord(extractApiData<UserDocumentRecord>(res.data))
  },

  async remove(docId: string | number): Promise<void> {
    await api.delete(API_PATHS.userDocuments.remove(docId))
  },
}
